/**
 * @file AuthHeadline.tsx
 * @brief The login headline: "Speak <language>. / Be heard in theirs.", the
 *        language from the visitor's region (headlineLanguages.ts), flipping
 *        through the country's languages when it has several; "One table. /
 *        Many tongues." when there is no region to go on.
 *
 * @details
 * DES 03-10-2026. The word flips like the Flip board (a short rotateX in,
 * `auth-lang-flip` in index.css), on the promise scroller's rhythm (2.6s).
 * It stops on the first language after a minute, as the question marquee
 * pauses, and never moves with reduced motion. One language: no flip.
 *
 * @author DES
 * @date 03-10-2026
 */

import { useEffect, useState } from "react";
import { headlineLanguagesHere } from "./headlineLanguages";

const HOLD_MS = 2600;
const STOP_AFTER_MS = 60_000;

export function AuthHeadline() {
  const [languages] = useState(headlineLanguagesHere);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (languages.length < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > STOP_AFTER_MS) {
        window.clearInterval(id);
        setIndex(0);
        return;
      }
      setIndex((i) => (i + 1) % languages.length);
    }, HOLD_MS);
    return () => window.clearInterval(id);
  }, [languages]);

  if (languages.length === 0) {
    return (
      <h1 className="text-4xl font-bold tracking-tight xl:text-5xl">
        One table.{" "}
        <br />
        <span className="text-brand">Many tongues.</span>
      </h1>
    );
  }

  const shown = languages[index] ?? languages[0];
  return (
    <h1
      className="text-4xl font-bold tracking-tight xl:text-5xl"
      // Screen readers get the whole list once, not a word that keeps changing.
      aria-label={`Speak ${languages.join(", ")}. Be heard in theirs.`}
    >
      <span aria-hidden>
        Speak{" "}
        <span className="auth-lang-flip" key={shown}>
          {shown}
        </span>
        .{" "}
        <br />
        <span className="text-brand">Be heard in theirs.</span>
      </span>
    </h1>
  );
}
